import React, { useState, useEffect } from 'react';
import { UserPlus, UserCheck, UserX, Loader } from 'lucide-react';
import { sendFriendRequest, getFriendStatus, updateFriendRequest, FriendStatus } from '../lib/friends';
import { supabase } from '../lib/supabase';

interface FriendRequestButtonProps {
  userId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const FriendRequestButton: React.FC<FriendRequestButtonProps> = ({ userId, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<FriendStatus | null>(null);
  const [friendRequestId, setFriendRequestId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (userId && isAuthenticated) {
      checkFriendStatus();
    }
  }, [userId, isAuthenticated]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    } catch (err) {
      console.error('Error checking auth:', err);
      setIsAuthenticated(false);
    }
  };

  const checkFriendStatus = async () => {
    try {
      const friendRequest = await getFriendStatus(userId);
      if (friendRequest) {
        setStatus(friendRequest.status);
        setFriendRequestId(friendRequest.id);
      } else {
        setStatus(null);
        setFriendRequestId(null);
      }
    } catch (err) {
      console.error('Error checking friend status:', err);
      onError?.(err instanceof Error ? err : new Error('Failed to check friend status'));
    }
  };

  const handleFriendRequest = async () => {
    if (!userId || !isAuthenticated) return;
    
    try {
      setLoading(true);

      if (!status) {
        // Send a new friend request
        const request = await sendFriendRequest(userId);
        setStatus('pending');
        setFriendRequestId(request.id);
        onSuccess?.();
      } else if (friendRequestId) {
        // Update existing request
        const newStatus: FriendStatus = status === 'pending' ? 'rejected' : 'pending';
        const updatedRequest = await updateFriendRequest(friendRequestId, newStatus);
        setStatus(updatedRequest.status);
        onSuccess?.();
      }
    } catch (err) {
      console.error('Error handling friend request:', err);
      onError?.(err instanceof Error ? err : new Error('Failed to handle friend request'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <button 
        disabled
        className="flex-1 flex items-center justify-center space-x-1 bg-gray-300 text-white px-3 py-2 rounded-md cursor-not-allowed"
      >
        <UserPlus className="h-4 w-4" />
        <span>Sign in to Add Friend</span>
      </button>
    );
  }

  if (loading) {
    return (
      <button 
        disabled
        className="flex-1 flex items-center justify-center space-x-1 bg-gray-300 text-white px-3 py-2 rounded-md cursor-not-allowed"
      >
        <Loader className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </button>
    );
  }

  if (status === 'accepted') {
    return (
      <button 
        disabled
        className="flex-1 flex items-center justify-center space-x-1 bg-green-500 text-white px-3 py-2 rounded-md cursor-not-allowed"
      >
        <UserCheck className="h-4 w-4" />
        <span>Friends</span>
      </button>
    );
  }

  if (status === 'pending') {
    return (
      <button 
        onClick={handleFriendRequest}
        className="flex-1 flex items-center justify-center space-x-1 bg-yellow-500 text-white px-3 py-2 rounded-md hover:bg-yellow-600 transition"
      >
        <UserX className="h-4 w-4" />
        <span>Cancel Request</span>
      </button>
    );
  }

  return (
    <button 
      onClick={handleFriendRequest}
      className="flex-1 flex items-center justify-center space-x-1 bg-orange-500 text-white px-3 py-2 rounded-md hover:bg-orange-600 transition"
    >
      <UserPlus className="h-4 w-4" />
      <span>Add Friend</span>
    </button>
  );
};

export default FriendRequestButton;